/**
 * Intercom widget loader.
 *
 * `IntercomProvider` only calls `window.Intercom('boot', …)`, and the funnel never shipped the snippet
 * that defines it — so the provider was registered, the config was set, and nothing happened: the `?.`
 * swallowed every call. WebQuiz and WebPaywalls hardcode the snippet in their HTML; here it is loaded
 * from `appConfig.intercom`, so the app id lives in one place.
 *
 * The stub queues calls made before the widget script arrives, which is what lets `boot` run during
 * provider init without waiting for the network.
 */
type IntercomFn = ((...args: unknown[]) => void) & { q?: unknown[][]; c?: (args: unknown) => void };

interface IntercomWindow {
  Intercom?: IntercomFn;
  intercomSettings?: Record<string, unknown>;
}

let loaded = false;

export const loadIntercomWidget = (appId: string | undefined): void => {
  if (!appId || loaded || typeof window === 'undefined') return;
  loaded = true;
  const target = window as unknown as IntercomWindow;
  const existing = target.Intercom;
  if (typeof existing === 'function') {
    existing('reattach_activator');
    existing('update', target.intercomSettings);
    return;
  }
  const queue: unknown[][] = [];
  const stub = ((...args: unknown[]) => {
    queue.push(args);
  }) as IntercomFn;
  stub.q = queue;
  stub.c = (args: unknown) => {
    queue.push(args as unknown[]);
  };
  target.Intercom = stub;
  const append = () => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    document.head.appendChild(script);
  };
  if (document.readyState === 'complete') append();
  else window.addEventListener('load', append, false);
};

/** Tests only. */
export const resetIntercomWidget = (): void => {
  loaded = false;
};
